import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import { action, autorun, makeObservable, observable, reaction } from 'mobx';

import i18n from '../../i18n/config';
import { getFirebaseAuthToken } from '../../services/firebase';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { trackJackpotWidgetOpen } from '../jackpot/jackpotAnalytics';
import { applyJackpotFlagOverride } from '../jackpot/resolveJackpotFlag';
import type { JackpotCoinType } from '../jackpot/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

export class GameUiStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    window.addEventListener('message', this.onMessageEvent);

    ldClient?.on('ready', () => {
      this.setNewGameUiAuthEnabled(resolveFlag('game_ui_new_authentication', true));
    });

    ldClient?.on('change', (changes) => {
      if ('game_ui_new_authentication' in changes) {
        this.setNewGameUiAuthEnabled(resolveFlag('game_ui_new_authentication', true));
      }
    });

    autorun(() => {
      if (this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized) {
        this.setNewGameUiAuthEnabled(resolveFlag('game_ui_new_authentication', true));
      }
    });
  }

  @observable
  public newGameUiAuthEnabled: boolean = false;

  @action
  public setNewGameUiAuthEnabled(flag: boolean) {
    this.newGameUiAuthEnabled = flag;
  }

  public getGameUiURL = (path: string, params: Record<string, string | number | boolean | undefined> = {}) => {
    const allParams = {
      ...params,
      platform: 'WEB',
      lang: i18n.language,
      version: this.newGameUiAuthEnabled ? '3' : '2',
    };
    return `${import.meta.env.VITE_GAME_UI_URL}/${path}?${new URLSearchParams(omitBy(allParams, isUndefined)).toString()}`;
  };

  public openGameUiDialog = (path: string, params: Record<string, string | number | boolean | undefined> = {}) => {
    const { dataTestId } = params;
    const url = this.getGameUiURL(path, params);
    this.rootStore.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenGameUiDialog, options: { url, dataTestId } });
  };

  public openCashOfferDialog = () => {
    const offer = this.rootStore.walletStore.balance.cashOffer;
    this.openGameUiDialog('cash-offer', {
      dataTestId: 'cash-offer-dialog-iframe',
      ...(offer && {
        cashOfferPrice: offer.price,
        cashOfferSc: offer.scAmount,
        cashOfferGc: offer.gcAmount,
        cashOfferGcPerDollar: offer.gcPerDollar,
      }),
    });
  };

  /**
   * game-ui owns the jackpot popup itself (FE_DESIGN_SPEC §1) — this only opens it. Mirrors
   * openCashOfferDialog: the context is read off the store rather than passed in, so every entry
   * point (category row, promo card, nav icon, crown badges) opens the same dialog the same way.
   *
   * No longer the only route into opt-in: the promo card's `OPT IN` CTA now posts participation
   * itself (`JackpotStore.optIn`). Everything else still belongs here — opting OUT, PLAY NOW, the
   * ladder, the eligible games and How-It-Works — and the popup remains the surface a failed widget
   * opt-in falls back to.
   */
  public openJackpotDialog = ({
    trigger = 'click',
    source = 'card',
  }: { trigger?: 'click' | 'auto'; source?: 'cta' | 'card' } = {}) => {
    const { loadedCoinType, eligibilityForSelectedCoin, isOptedIn, topLevelAmount } = this.rootStore.jackpotStore;

    /*
     * The `lobby_widget` click events live here, not in the widgets. Four surfaces open this dialog and
     * the wide widget's CTA calls `stopPropagation`, so a handler on the rail wrapper alone misses the
     * tap players actually make. Tracking the open covers every current entry point and every future one.
     *
     * `auto` is the jump-paced auto-open (`JackpotStore.maybeAutoOpenPopup`): a real open, but nobody
     * clicked, so reporting it as a click would inflate lobby CTR. game-ui still reports the resulting
     * `popup` impression either way.
     */
    if (trigger === 'click') {
      // `loadedCoinType` is the group actually being opened; the lobby's own toggle can be a frame ahead.
      trackJackpotWidgetOpen(loadedCoinType ?? null, isOptedIn, topLevelAmount, source);
    }

    this.openGameUiDialog('jackpot', {
      dataTestId: 'jackpot-dialog-iframe',
      /*
       * The page the player is actually on. Every Crown Jackpot event carries `page_url`, and game-ui
       * runs at its own `/jackpot` URL — without this, every event it sends would report the jackpot
       * surface instead of the lobby.
       */
      pageUrl: window.location.href,
      // Jackpots are per coin group, so the popup is opened for one coin — never a two-tab dialog.
      ...(loadedCoinType && { coinType: loadedCoinType }),
      ...(eligibilityForSelectedCoin?.jackpotId && { jackpotId: eligibilityForSelectedCoin.jackpotId }),
    });

    this.refreshJackpotOnDialogClose();
  };

  /**
   * Opens game-ui for a WIN and nothing else — `?winOnly=1`.
   *
   * Not `openJackpotDialog`: that route draws the lobby popup, and a player who has just won money is
   * owed their prize, not an information screen they did not ask for. In this mode the route renders
   * nothing and the ceremony (`JackpotWinFlowHost`, mounted app-level in game-ui) is the only thing on
   * screen; it reports no popup impression, makes no overview request, and asks us to close the dialog
   * when the win is claimed.
   *
   * No click event either — nobody tapped anything. The lobby found the win by polling.
   */
  public openJackpotWinDialog = () => {
    const { loadedCoinType } = this.rootStore.jackpotStore;

    this.openGameUiDialog('jackpot', {
      dataTestId: 'jackpot-win-dialog-iframe',
      winOnly: '1',
      // Every Crown Jackpot event carries `page_url`, and game-ui runs at its own URL.
      pageUrl: window.location.href,
      ...(loadedCoinType && { coinType: loadedCoinType }),
    });

    // The claim moves the wallet and can change participation state; refresh on close like the popup.
    this.refreshJackpotOnDialogClose();
  };

  // Disposer for the pending close-reaction below. Reopening the popup replaces it rather than
  // stacking a second reaction (and a second refetch) on top of the first.
  /**
   * Game the jackpot popup's `PLAY NOW` asked us to launch, or `null`.
   *
   * Parked here rather than navigated to directly: a store has no router. `GameUiDialog` reacts to
   * this, navigates, and calls {@link clearJackpotGameLaunch} — it is mounted whenever this can be
   * set, because the message can only come from the dialog it is about.
   */
  @observable
  public pendingJackpotGame: { gameId: number; coinType: JackpotCoinType } | null = null;

  @action
  private setPendingJackpotGame = (game: { gameId: number; coinType: JackpotCoinType }) => {
    this.pendingJackpotGame = game;
  };

  @action
  public clearJackpotGameLaunch = () => {
    this.pendingJackpotGame = null;
  };

  private disposeJackpotDialogClose: (() => void) | null = null;

  /**
   * The popup is the single opt-in surface, so a participation change made inside it is invisible to
   * the lobby until the shared store refetches. Refreshing on close updates every jackpot widget
   * together — category row, promo card, nav icon, crown badges. Same one-shot close-reaction the
   * CashOfferStore uses; it also covers the popup being dismissed before its
   * `jackpotParticipationChanged` message got out.
   */
  private refreshJackpotOnDialogClose = () => {
    this.disposeJackpotDialogClose?.();

    const dispose = reaction(
      () => this.rootStore.uiStore.openDialog.dialog === EnumOpenDialog.isOpenGameUiDialog,
      (isOpen: boolean) => {
        if (!isOpen) {
          dispose();
          this.disposeJackpotDialogClose = null;
          void this.rootStore.jackpotStore.fetchEligibility();
        }
      },
    );

    this.disposeJackpotDialogClose = dispose;
  };

  public closeGameUiDialog = () => {
    this.rootStore.uiStore.setCloseDialog();
  };

  private onMessageEvent = (event: MessageEvent) => {
    const { data, origin, source } = event;

    if (origin !== import.meta.env.VITE_GAME_UI_URL) {
      return;
    }

    switch (data.type) {
      case 'getFlags':
        if (source && ldClient) {
          source.postMessage(
            {
              type: 'getFlags_response',
              payload: {
                // Raw LD values, except that the jackpot dev override is folded in: game-ui gates
                // its whole jackpot surface on `crown_jackpot_splash_feature` and posts `closeGame` when it
                // reads false, so a lobby forced on by the override must hand the iframe the same
                // answer or the popup shuts the moment it opens. No-op in production builds.
                flags: applyJackpotFlagOverride(ldClient.allFlags()),
              },
            },
            { targetOrigin: import.meta.env.VITE_GAME_UI_URL },
          );
        }
        break;
      case 'getIdToken': {
        void getFirebaseAuthToken().then((token) => {
          if (source && token) {
            source.postMessage(
              {
                type: 'getIdToken_response',
                payload: { token },
              },
              { targetOrigin: import.meta.env.VITE_GAME_UI_URL },
            );
          }
        });
        break;
      }
      // Crown Rips parallel launch: the iframe (opened before launch-game finished) asks for its
      // session; CrownRipsStore delivers it — or re-runs launch-game — via this iframe window.
      case 'crownRipsSessionRequest':
        this.rootStore.crownRipsStore.handleSessionRequest(source as Window | null);
        break;
      // game-ui -> host: the jackpot popup changed the player's participation. It is the only
      // surface that posts /jackpot/participation (FE_DESIGN_SPEC §7.4), so this is the lobby's
      // only signal short of the 30s poll — refetch so every jackpot widget updates while the
      // popup is still open, rather than only once it closes.
      case 'jackpotParticipationChanged':
        void this.rootStore.jackpotStore.fetchEligibility();
        break;
      /*
       * game-ui -> host -> game-ui: a jackpot win was claimed, relayed to our OTHER game-ui window.
       *
       * Two of them can hold the same PENDING win at once. `GameIframe` is mounted at the casino
       * shell and only HIDDEN while the player is in the lobby (it keeps its socket that way), so the
       * in-slot banner survives the trip with the win still queued; the jackpot dialog is a separate
       * document that fetches the same win and claims it. Neither can see the other, and the survivor
       * has no reason to refetch — its recovery read runs once per mount. Without this relay the
       * player comes back to the game and is still offered `CLAIM` for money already paid.
       *
       * Not sent back to the window it came from: a document that claimed the win has already
       * dropped it, and `dropClaimedWin` would be a no-op there anyway.
       */
      case 'jackpotWinClaimed': {
        const winId = (data.payload as { winId?: string } | undefined)?.winId;
        const gameIframeWindow = this.rootStore.gameStore.slotsGameUiIframe?.contentWindow;

        if (winId && source !== gameIframeWindow) {
          this.rootStore.gameStore.sendSlotsGameUiMessage({ type: 'jackpotWinClaimed', payload: { winId } });
        }
        break;
      }
      // game-ui -> host: PLAY NOW, with the game centred in the popup's eligible-games carousel.
      // game-ui cannot route to a slot from inside its iframe, so the launch is ours; the dialog is
      // torn down by whoever handles `pendingJackpotGame`, after navigation, so the player never
      // sees the lobby flash between the two.
      case 'jackpotPlayGame':
        // `data` is the whole envelope — the game rides in `payload`, as with every other
        // game-ui message that carries one.
        this.setPendingJackpotGame(data.payload as { gameId: number; coinType: JackpotCoinType });
        break;
      // Unknown types fall through deliberately: game-ui hosts several features on this one
      // channel and each posts its own messages, so anything unrecognised is another feature's.
    }
  };
}
